import { useTranslation } from "react-i18next";
import type { EditorialContent } from "@nh/shared";
import { ProductBlockHeading } from "./ProductBlockHeading";

export type ProductSpecTableSection = NonNullable<EditorialContent["specTable"]>;

export interface ProductDetailSpecTableProps {
  section: ProductSpecTableSection;
  number?: string;
}

/**
 * La tabla nutrimental — "06 / Composición" en el original.
 *
 * ── No es una `<table>`, y es a propósito ────────────────────────────
 * El original la pinta como una rejilla de dos a cuatro columnas donde
 * cada celda es un ingrediente con su cantidad y su función. No hay
 * cabecera de columna ni relación fila-columna que leer: no es una tabla
 * de datos cruzados, es una LISTA de definiciones con la cantidad al
 * lado. `<dl>` dice exactamente eso, y un lector de pantalla lo anuncia
 * como lista en vez de anunciar "tabla de 4 columnas" y prometer una
 * navegación por celdas que no lleva a ninguna parte.
 *
 * ── Las medidas, literales del original ──────────────────────────────
 *   caja      bg surface · borde `line` · radio 3 · overflow hidden
 *   rejilla   2 columnas (4 en `md`) · separación 1px sobre `line`
 *   celda     padding 16 · nombre SANS 15px/600 · -0.01em
 *   cantidad  mono 10px/600 · violet-pop sobre `mist` · 2/6 · radio 2
 *   función   13px · ink-3
 *   pie       mt-16 · 12px
 *
 * La separación de 1 px es el mismo truco que la ficha técnica: la
 * rejilla se pinta del color de la línea y cada celda tapa su hueco en
 * blanco, así que las divisiones salen solas sin un borde por celda que
 * se duplicaría en los encuentros.
 *
 * ── Dos contrastes subidos y un campo sin sitio ──────────────────────
 * La función del ingrediente va en `ink-3` (#3A2F55, 10.3:1) y el pie en
 * `ink-3` también; el original usa `ink-4` (#8A8377), que sobre blanco da
 * 3.48:1 y no llega al 4.5:1 que pide un texto de 12 px.
 *
 * `specTable.type` —"Información nutrimental", "Análisis por porción"—
 * existe en el contrato y el renderizador original NO lo pinta en ningún
 * sitio. Aquí se pinta como rótulo del bloque en lugar del genérico
 * "Composición": es el único hueco donde cabe sin inventarse una fila.
 */
export function ProductDetailSpecTable({ section, number }: ProductDetailSpecTableProps) {
  const { t } = useTranslation();

  return (
    <section className="grid gap-7">
      <ProductBlockHeading
        eyebrow={section.type ?? t("Composición")}
        number={number}
        title={section.title}
      />

      <div className="overflow-hidden rounded-[3px] border border-ds-border bg-white">
        <dl className="grid grid-cols-2 gap-px bg-ds-border md:grid-cols-4">
          {section.items.map((row) => (
            <div key={row.name} className="bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-[15px] font-semibold leading-tight tracking-[-0.01em] text-ds-ink">
                  {row.name}
                </dt>
                {row.dose && (
                  <dd className="shrink-0 rounded-[2px] bg-ds-mist px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none text-ds-violet-pop">
                    {row.unit ? `${row.dose} ${row.unit}` : row.dose}
                  </dd>
                )}
              </div>
              {row.function && (
                <dd className="mt-1.5 text-[12px] leading-[1.45] text-ds-ink-3">
                  {row.function}
                </dd>
              )}
            </div>
          ))}
        </dl>
      </div>

      {section.footer && (
        <p className="-mt-3 text-[12px] leading-[1.5] text-ds-ink-3">{section.footer}</p>
      )}
    </section>
  );
}
